import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type {
  ExportJob,
  ExportStatus,
} from "../components/timeline/hooks/useTimelineExports";

type AddMessageListener = (listener: (message: any) => void) => () => void;

/** Map WebSocket status strings to ExportJob status */
const WS_STATUS_MAP: Record<string, ExportStatus> = {
  downloading: "downloading",
  encoding: "encoding",
  uploading: "uploading",
  completed: "completed",
  failed: "failed",
  cancelled: "cancelled",
};

/**
 * Listens for timeline-export WebSocket events and updates the
 * TanStack Query cache directly for real-time progress, or invalidates
 * on terminal events (completed/failed/cancelled) to fetch full data.
 *
 * Handles:
 * - timeline-export-progress   — updates progress in-place (no refetch)
 * - timeline-export-completed  — invalidates cache to fetch output asset URL
 * - timeline-export-failed     — invalidates cache to fetch error details
 * - timeline-export-cancelled  — invalidates cache to fetch final state
 *
 * Must be mounted alongside useWebSocket() to receive events.
 */
export function useTimelineExportEvents(
  addMessageListener: AddMessageListener | undefined,
) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!addMessageListener) return;

    const removeListener = addMessageListener((message) => {
      const eventType = message?.eventType as string | undefined;
      if (!eventType) return;

      const data = message?.eventData as Record<string, unknown> | undefined;
      if (!data) return;

      const exportId = data.exportId as string | undefined;
      if (!exportId) return;

      if (eventType === "timeline-export-progress") {
        // Update progress in-place without refetching from DB
        const progress = data.progress as number | undefined;
        const status = data.status as string | undefined;

        queryClient.setQueriesData<ExportJob[]>(
          { queryKey: ["timeline-exports"] },
          (old) => {
            if (!old) return old;
            return old.map((job) =>
              job.id === exportId
                ? {
                    ...job,
                    progress: progress ?? job.progress,
                    status:
                      (status ? WS_STATUS_MAP[status] : undefined) ??
                      job.status,
                  }
                : job,
            );
          },
        );
        return;
      }

      if (
        eventType === "timeline-export-completed" ||
        eventType === "timeline-export-failed" ||
        eventType === "timeline-export-cancelled"
      ) {
        // Terminal events: invalidate to fetch full data (output URL, error details)
        queryClient.invalidateQueries({
          queryKey: ["timeline-exports"],
        });
        return;
      }
    });

    return removeListener;
  }, [addMessageListener, queryClient]);
}
